import { FUND_LANGUAGES, t, setStoredFundLang, resolveFundLang } from './fundI18n.js';
import { calcFeeBreakdown } from './fundStore.js';

export function infoIcon(helpKey, fund) {
    const label = t(fund.locale, `help.${helpKey}`);
    return `<button type="button" class="fg-fund-info" data-help="${helpKey}" aria-label="More info" title="${escapeAttr(label)}">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
    </button>`;
}

function escapeAttr(s) {
    return String(s).replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

export function labelWithHelp(text, helpKey, fund) {
    return `<span class="fg-fund-label-row">${text} ${infoIcon(helpKey, fund)}</span>`;
}

export function attachInfoPopovers(root, fund) {
    if (!root) return;
    let pop = root.querySelector('.fg-fund-info-popover');
    if (!pop) {
        pop = document.createElement('div');
        pop.className = 'fg-fund-info-popover';
        pop.setAttribute('role', 'tooltip');
        pop.hidden = true;
        root.appendChild(pop);
    }

    const hide = () => {
        pop.hidden = true;
        pop.textContent = '';
    };

    root.querySelectorAll('.fg-fund-info').forEach((btn) => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const key = btn.dataset.help;
            const text = t(fund.locale, `help.${key}`);
            if (pop.hidden || pop.dataset.key !== key) {
                pop.dataset.key = key;
                pop.textContent = text;
                pop.hidden = false;
                const r = btn.getBoundingClientRect();
                const rootR = root.getBoundingClientRect();
                pop.style.left = `${Math.min(r.left - rootR.left, root.clientWidth - 280)}px`;
                pop.style.top = `${r.bottom - rootR.top + 8}px`;
            } else hide();
        });
    });

    document.addEventListener('click', hide, { once: false });
    root.addEventListener('click', (e) => {
        if (!e.target.closest('.fg-fund-info') && !e.target.closest('.fg-fund-info-popover')) hide();
    });
}

export function showToast(message, type = 'success') {
    const variant = type === 'error' ? 'error' : 'success';
    let el = document.getElementById('fg-fund-toast');
    if (!el) {
        el = document.createElement('div');
        el.id = 'fg-fund-toast';
        el.className = 'fg-fund-toast';
        document.body.appendChild(el);
    }
    el.className = `fg-fund-toast fg-fund-toast--${variant}`;
    el.textContent = message;
    el.classList.add('visible');
    clearTimeout(el._t);
    el._t = setTimeout(() => el.classList.remove('visible'), 4200);
}

export function renderLangBar(fund) {
    const opts = FUND_LANGUAGES.map(
        (lang) => `<option value="${lang}" ${fund.profileLang === lang ? 'selected' : ''}>${lang}</option>`
    ).join('');
    return `
        <div class="fg-fund-lang-bar" dir="${fund.rtl ? 'rtl' : 'ltr'}">
            <label class="fg-fund-lang-bar__label" for="fg-fund-lang-select">🌐</label>
            <select id="fg-fund-lang-select" class="fg-fund-lang-bar__select" aria-label="Language">
                ${opts}
            </select>
        </div>`;
}

export function attachLangBar(root, fund, onChange) {
    const sel = root?.querySelector('#fg-fund-lang-select');
    sel?.addEventListener('change', () => {
        fund.profileLang = sel.value;
        setStoredFundLang(sel.value);
        Object.assign(fund, resolveFundLang(fund.profileLang));
        onChange?.();
    });
}

export function renderFeeVisualizer(goal, fund) {
    const { goal: g, stripe, platform, net } = calcFeeBreakdown(goal);
    return `
        <div class="fg-fund-fee-viz" id="fee-visualizer">
            <h4>${t(fund.locale, 'feeTitle')}</h4>
            <p class="fg-fund-fee-viz__line">${t(fund.locale, 'feeIfRaise')} <strong>€${g.toLocaleString()}</strong></p>
            <ul class="fg-fund-fee-viz__list">
                <li><span>${t(fund.locale, 'feeStripe')}</span><strong>~€${stripe.toFixed(2)}</strong></li>
                <li><span>${t(fund.locale, 'feePlatform')}</span><strong>€${platform.toFixed(2)}</strong></li>
            </ul>
            <p class="fg-fund-fee-viz__net">${t(fund.locale, 'feeYouReceive')} <strong>€${net.toFixed(0)}</strong></p>
        </div>`;
}

export function renderGallery(images = [], fund) {
    if (!images.length) return '';
    const slides = images
        .map(
            (url, i) =>
                `<div class="fg-fund-gallery__slide" style="background-image:url('${url.replace(/'/g, '%27')}')" role="img" aria-label="${t(fund.locale, 'galleryImage')} ${i + 1}"></div>`
        )
        .join('');
    return `<div class="fg-fund-gallery" role="region" aria-label="Campaign images">
        <div class="fg-fund-gallery__track">${slides}</div>
        <div class="fg-fund-gallery__dots">${images.map((_, i) => `<span class="${i === 0 ? 'active' : ''}" data-i="${i}"></span>`).join('')}</div>
    </div>`;
}

export function attachGallery(root) {
    const gallery = root?.querySelector('.fg-fund-gallery');
    if (!gallery) return;
    const track = gallery.querySelector('.fg-fund-gallery__track');
    const dots = gallery.querySelectorAll('.fg-fund-gallery__dots span');
    let index = 0;
    const show = (i) => {
        index = i;
        track.style.transform = `translateX(-${i * 100}%)`;
        dots.forEach((d, j) => d.classList.toggle('active', j === i));
    };
    dots.forEach((d) => d.addEventListener('click', () => show(Number(d.dataset.i))));
    let startX = 0;
    gallery.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
    gallery.addEventListener('touchend', (e) => {
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) show(dx < 0 ? Math.min(index + 1, dots.length - 1) : Math.max(index - 1, 0));
    }, { passive: true });
}

export function renderFaqAccordion(faqs, fund) {
    if (!faqs?.length) return '';
    const items = faqs
        .map(
            (f, i) => `
        <details class="fg-fund-faq-item" ${i === 0 ? 'open' : ''}>
            <summary>${escapeHtml(f.question)}</summary>
            <p>${escapeHtml(f.answer)}</p>
        </details>`
        )
        .join('');
    return `<section class="fg-fund-detail__section"><h3>${t(fund.locale, 'faq')}</h3>${items}</section>`;
}

function escapeHtml(s) {
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

export function renderCountdown(endDate, fund) {
    const end = new Date(endDate);
    const now = new Date();
    if (end <= now) return `<p class="fg-fund-countdown fg-fund-countdown--ended">${t(fund.locale, 'closed')}</p>`;
    const ms = end - now;
    const d = Math.floor(ms / 86400000);
    const h = Math.floor((ms % 86400000) / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return `<div class="fg-fund-countdown" aria-live="polite">
        <span class="fg-fund-countdown__label">${t(fund.locale, 'countdown')}</span>
        <span class="fg-fund-countdown__vals"><strong>${d}</strong>d <strong>${h}</strong>h <strong>${m}</strong>m</span>
    </div>`;
}
